import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Divider,
  Chip,
} from "@mui/material";
import {
  CalendarToday,
  People,
  Assignment,
  QueuePlayNext,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Styles object
const styles = {
  sharedCard: {
    mb: 2,
    boxShadow: "0 10px 25px rgba(0, 23, 231, 0.08)",
    borderRadius: 3,
    bgcolor: "#ffffff",
    overflow: "hidden",
    border: "1px solid rgba(0, 23, 231, 0.1)",
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  },
  blueStrip: {
    height: 6,
    background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
    width: "100%",
  },
  sharedCardTitle: {
    color: "#0017E7",
    fontWeight: 700,
    fontSize: { xs: "1.75rem", md: "2.25rem" },
  },
  detailsContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: 4,
    mb: 4,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    mb: 2,
    color: "#0017E7",
    fontWeight: 600,
  },
  largeIcon: { mr: 1, fontSize: 20, color: "#0017E7" },
  outlineButton: {
    borderColor: "#0017E7",
    color: "#0017E7",
    borderRadius: 2,
    "&:hover": {
      borderColor: "#0010a3",
      bgcolor: "rgba(0, 23, 231, 0.04)",
    },
  },
  primaryButton: {
    background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
    "&:hover": {
      background: "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
      transform: "translateY(-1px)",
    },
    borderRadius: 2,
    boxShadow: "0 2px 4px rgba(0, 23, 231, 0.2)",
    transition: "all 0.2s ease-in-out",
    "&.Mui-disabled": {
      bgcolor: "#e2e8f0",
      color: "#94a3b8",
    },
  },
  statusMessage: (isSuccess) => ({
    mt: 1,
    p: 1,
    borderRadius: 1,
    fontSize: "0.75rem",
    display: "block",
    bgcolor: isSuccess ? "#dcfce7" : "#fee2e2",
    color: isSuccess ? "#166534" : "#b91c1c",
  }),
};

const SharedCarnivalView = ({
  carnival,
  isAuthenticated,
  hasTicket,
  ticket,
  carnivalState,
  handleFileSelect,
  handleGetTicket,
  isDeadlinePassed,
  handleLogin,
  navigate,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 900,
        mx: "auto",
        bgcolor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <Card sx={styles.sharedCard}>
        <Box sx={styles.blueStrip} />
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={styles.sharedCardTitle}
            >
              {carnival.title}
            </Typography>
            {carnival.is_archived && (
              <Chip
                label={t("candidates.archived")}
                color="default"
                size="small"
                sx={{
                  mb: 2,
                  bgcolor: "rgba(0, 23, 231, 0.05)",
                  color: "#0017E7",
                  fontWeight: 500,
                }}
              />
            )}
            <Typography variant="body1" paragraph sx={{ color: "#475569" }}>
              {carnival.description}
            </Typography>
          </Box>

          <Divider sx={{ my: 3, borderColor: "#e2e8f0" }} />

          <Box sx={styles.detailsContainer}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" component="h3" sx={styles.sectionTitle}>
                <CalendarToday sx={styles.largeIcon} /> {t("candidates.importantDates")}
              </Typography>
              <Box sx={{ ml: 3, bgcolor: "#f1f5f9", p: 2, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ mb: 1, color: "#334155" }}>
                  <strong>{t("candidates.deadline")}:</strong>{" "}
                  {format(
                    new Date(carnival.deadline),
                    "MMM dd, yyyy 'at' h:mm a"
                  )}
                </Typography>
                <Typography variant="body1" sx={{ color: "#334155" }}>
                  <strong>{t("candidates.startTime")}:</strong>{" "}
                  {carnival.start_time
                    ? format(
                        new Date(carnival.start_time),
                        "MMM dd, yyyy 'at' h:mm a"
                      )
                    : t("candidates.toBeAnnounced")}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" component="h3" sx={styles.sectionTitle}>
                <People sx={styles.largeIcon} /> {t("candidates.participation")}
              </Typography>
              <Box sx={{ ml: 3, bgcolor: "#f1f5f9", p: 2, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ mb: 1, color: "#334155" }}>
                  <strong>{t("candidates.maximumParticipants")}:</strong>{" "}
                  {carnival.max_participants}
                </Typography>
              </Box>
            </Box>
          </Box>

          {isAuthenticated ? (
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3, borderColor: "#e2e8f0" }} />
              {hasTicket ? (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    bgcolor: "rgba(0, 23, 231, 0.05)",
                    border: "1px solid rgba(0, 23, 231, 0.1)",
                    borderRadius: 3,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 6,
                      height: "100%",
                      background:
                        "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
                    }}
                  />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#0017E7", fontWeight: 600 }}
                  >
                    {t("candidates.yourTicketInformation")}
                  </Typography>
                  <Typography sx={{ mb: 2, color: "#334155" }}>
                    {t("candidates.queuePosition")}: <strong>{ticket.queue_position}</strong>
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const encodedId = btoa(ticket.id.toString());
                      navigate(`/interview-room/${encodedId}`);
                    }}
                    sx={styles.primaryButton}
                  >
                    {t("candidates.enterInterviewRoom")}
                  </Button>
                </Box>
              ) : !isDeadlinePassed(carnival.deadline) ? (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#0017E7", fontWeight: 600 }}
                  >
                    {t("candidates.submitYourApplication")}
                  </Typography>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileSelect(e, carnival.id)}
                    style={{ display: "none" }}
                    id={`cv-upload-${carnival.id}`}
                  />
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <label htmlFor={`cv-upload-${carnival.id}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<Assignment />}
                        sx={styles.outlineButton}
                      >
                        {t("candidates.selectCv")}
                      </Button>
                    </label>
                    {carnivalState.selectedFile && (
                      <Typography sx={{ color: "#475569" }}>
                        {t("candidates.selected")}: {carnivalState.selectedFile.name}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => handleGetTicket(carnival.id)}
                      disabled={!carnivalState.selectedFile}
                      startIcon={<QueuePlayNext />}
                      sx={styles.primaryButton}
                    >
                      {t("candidates.getTicket")}
                    </Button>
                  </Box>
                  {carnivalState.status && (
                    <Typography
                      sx={styles.statusMessage(
                        carnivalState.status.includes("successfully")
                      )}
                    >
                      {carnivalState.status}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    bgcolor: "#fee2e2",
                    borderRadius: 3,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 6,
                      height: "100%",
                      bgcolor: "#dc2626",
                    }}
                  />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#991b1b", fontWeight: 600 }}
                  >
                    {t("candidates.applicationClosed")}
                  </Typography>
                  <Typography sx={{ color: "#7f1d1d" }}>
                    {t("candidates.deadlinePassedMessage")}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Divider sx={{ mb: 3, borderColor: "#e2e8f0" }} />
              <Typography variant="body1" sx={{ mb: 3, color: "#475569" }}>
                {t("candidates.signInToParticipate")}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleLogin}
                sx={{ ...styles.primaryButton, px: 4, py: 1 }}
              >
                {t("candidates.signInRegister")}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Button
          variant="outlined"
          onClick={() => navigate("/")}
          sx={styles.outlineButton}
        >
          {t("candidates.backToHome")}
        </Button>
      </Box>
    </Box>
  );
};

export default SharedCarnivalView;
